import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import jobOfferService from "../../services/jobOfferService";
import { getErrorMessage } from "../../utils/errorHandler";
import PageHeader from "../../components/ui/PageHeader";
import FilterBar from "../../components/ui/FilterBar";
import { Plus, Edit2, Trash2, MapPin, Calendar, Briefcase, DollarSign } from "lucide-react";
import "../../styles/recruitment.css";
import "../../styles/dashboard.css";

const JobOffers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const response = await jobOfferService.getAll();
      const offersList = Array.isArray(response) 
        ? response 
        : (response?.data || []);
      setOffers(offersList);
    } catch (err) {
      console.error("Error loading offers:", err);
      toast.error(getErrorMessage(err));
      setOffers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (offer) => {
    navigate(`/rh/job-offers/${offer.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("èŠtes-vous sè»r de vouloir supprimer cette offre ?")) {
      try {
        await jobOfferService.delete(id);
        toast.success("Offre supprimée.");
        loadOffers();
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
    }
  };

  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          offer.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          offer.skills?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || offer.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div style={{ padding: "24px" }}>
      <PageHeader
        title="Offres d'Emploi"
        subtitle="Gérez les postes ouverts au recrutement et suivez leur statut de publication."
        actions={
          <button
            onClick={() => navigate("/rh/job-offers/new")}
            className="btn btn-primary"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <Plus size={18} />
            <span>Créer une offre</span>
          </button>
        }
      />

          {/* Filter Bar */}
          <FilterBar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            placeholder="Rechercher par intitule, compétence, mot-clé..."
            filters={[
              {
                id: "status",
                label: "Statut",
                value: filterStatus,
                onChange: setFilterStatus,
                options: [
                  { value: "all", label: "Toutes les offres" },
                  { value: "OPEN", label: "Ouverte" },
                  { value: "CLOSED", label: "Fermée" },
                  { value: "DRAFT", label: "Brouillon" }
                ]
              }
            ]}
            onReset={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
          />

          {/* Offers Cards Grid */}
          {loading ? (
            <div className="ui-card" style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
              Chargement des offres d'emploi...
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="ui-card" style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
              Aucune offre d'emploi disponible.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "20px" }}>
              {filteredOffers.map((offer) => {
                const isOpen = offer.status === "OPEN";
                return (
                  <div key={offer.id} className="ui-card ui-card-interactive" style={{ padding: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                        <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--text-main)", margin: 0 }}>
                          {offer.title}
                        </h3>
                        <span style={{
                          padding: "4px 10px",
                          borderRadius: "var(--radius-pill)",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          backgroundColor: isOpen ? "var(--status-approved-bg)" : "var(--status-rejected-bg)",
                          color: isOpen ? "var(--status-approved-text)" : "var(--status-rejected-text)"
                        }}>
                          {offer.status || "OPEN"}
                        </span>
                      </div>

                      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Briefcase size={14} /> {offer.contract_type} ({offer.experience_level})
                        </div>
                        {offer.location && (
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <MapPin size={14} /> {offer.location}
                          </div>
                        )}
                        {offer.salary && (
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            <DollarSign size={14} /> {offer.salary}
                          </div>
                        )}
                      </div>

                      <p style={{ fontSize: "0.88rem", color: "var(--text-secondary)", lineHeight: "1.5", marginBottom: "16px", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {offer.description}
                      </p>

                      {offer.skills && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "16px" }}>
                          {offer.skills.split(',').map((skill, i) => (
                            <span key={i} style={{ padding: "2px 8px", backgroundColor: "var(--primary-light)", color: "var(--primary)", borderRadius: "var(--radius-sm)", fontSize: "0.78rem", fontWeight: "600" }}>
                              {skill.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: "12px", marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>
                        <Calendar size={14} /> {offer.deadline ? `Clôture: ${new Date(offer.deadline).toLocaleDateString('fr-FR')}` : "Sans limite"}
                      </span>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button onClick={() => handleEdit(offer)} className="btn btn-icon" title="Modifier">
                          <Edit2 size={16} style={{ color: "var(--text-muted)" }} />
                        </button>
                        <button onClick={() => handleDelete(offer.id)} className="btn btn-icon" title="Supprimer">
                          <Trash2 size={16} style={{ color: "var(--status-rejected-text)" }} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
    </div>
  );
};

export default JobOffers;

